import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Clock, Lock, Printer, ArrowRight, XCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/site/StatusBadge";
import { MessageThread } from "@/components/site/MessageThread";
import { AccountSettings } from "@/components/site/AccountSettings";
import { Spinner } from "@/components/site/ProtectedRoute";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { CURRICULUM } from "@/lib/constants";
import { BookingCard } from "@/components/site/BookingCard";
import { SharedInsight, useMyInsights } from "@/components/course/AiInsights";
import { CertificateLinks } from "@/pages/CertificatePage";
import { ReviewForm } from "@/components/site/Reviews";
import { MyOnlineCourses } from "@/components/avenues/AvenueParts";

function NoApplication() {
  return (
    <div className="bg-white rounded-3xl border border-[#0B2A4A]/10 p-8 sm:p-10" data-testid="status-no-application">
      <h2 className="text-2xl font-bold text-[#0B2A4A]">Apply to unlock your course</h2>
      <p className="mt-3 text-slate-600 max-w-xl">Complete the short application for the 7-day Become a Digital Nomad for Beginners course. Mr. Greg reviews each application before lessons unlock.</p>
      <Link to="/apply" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-6 py-3 font-semibold" data-testid="dashboard-apply-btn">Apply for the Course <ArrowRight className="h-4 w-4" /></Link>
    </div>
  );
}

function PendingOrDeclined({ app }) {
  const pending = app.status === "pending";
  return (
    <div className={`rounded-3xl p-8 sm:p-10 border ${pending ? "bg-orange-50 border-orange-200" : "bg-rose-50 border-rose-200"}`} data-testid={`status-${app.status}`}>
      <div className="flex items-center gap-3">
        {pending ? <Clock className="h-6 w-6 text-[#F08A3C]" /> : <XCircle className="h-6 w-6 text-rose-600" />}
        <StatusBadge status={app.status} testId="application-status-badge" />
      </div>
      <h2 className="mt-4 text-2xl font-bold text-[#0B2A4A]">{pending ? "Your application is pending review" : "Your application was not approved"}</h2>
      <p className="mt-3 text-slate-600 max-w-xl">
        {pending ? "Thanks for applying. Lessons stay locked until Mr. Greg approves your application. You'll get an alert here (and a push notification if enabled)." : "Thank you for applying. You can message Mr. Greg in the Messages tab with any questions."}
      </p>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 opacity-70" data-testid="locked-days-list">
        {CURRICULUM.map((c) => (
          <div key={c.day} className="rounded-xl bg-white border border-[#0B2A4A]/10 p-4 text-sm flex items-start gap-2" data-testid={`locked-day-${c.day}`}>
            <Lock className="h-4 w-4 mt-0.5 shrink-0" />
            <span><span className="font-semibold">Day {c.day}</span><span className="block text-xs text-slate-500 mt-0.5">{c.title}</span></span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CourseGrid({ progress }) {
  const byDay = Object.fromEntries(progress.map((p) => [p.day, p]));
  const done = progress.filter((p) => p.status === "complete").length;
  const myInsights = useMyInsights();
  const [certs, setCerts] = useState([]);
  useEffect(() => { api.get("/me/certificates").then((r) => setCerts(r.data)).catch(() => {}); }, []);
  return (
    <div className="flex flex-col gap-8" data-testid="course-grid">
      <div className="bg-white rounded-3xl border border-[#0B2A4A]/10 p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-6 justify-between">
        <div className="flex-1">
          <StatusBadge status="approved" testId="application-status-badge" />
          <h2 className="mt-3 text-2xl font-bold text-[#0B2A4A]">Become a Digital Nomad for Beginners</h2>
          <div className="mt-4 flex items-center gap-4">
            <Progress value={(done / 7) * 100} className="h-2.5 max-w-sm" />
            <span className="font-mono-ui text-sm text-slate-600" data-testid="course-progress-text">{done}/7 complete</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/print/worksheets?mode=filled" className="inline-flex items-center gap-2 rounded-xl border border-[#0B2A4A]/20 px-4 py-2.5 text-sm font-semibold" data-testid="print-all-worksheets-btn"><Printer className="h-4 w-4" /> Print all my worksheets</Link>
          <Link to="/print/worksheets?mode=blank" className="inline-flex items-center gap-2 rounded-xl border border-[#0B2A4A]/20 px-4 py-2.5 text-sm font-semibold" data-testid="print-blank-worksheets-btn"><Printer className="h-4 w-4" /> Blank worksheets</Link>
        </div>
      </div>
      <CertificateLinks certs={certs} />
      {certs.length > 0 && <ReviewForm />}
      <BookingCard />
      <SharedInsight insight={myInsights.find((i) => i.scope === "course")} title="Your course summary from Mr. Greg" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {CURRICULUM.map((c) => (
          <Link key={c.day} to={`/course/day/${c.day}`} className="group bg-white rounded-2xl border border-[#0B2A4A]/10 p-6 hover:shadow-lg hover:-translate-y-0.5 transition-transform" data-testid={`student-day-card-${c.day}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono-ui text-sm font-bold text-[#F08A3C]">DAY {c.day}</span>
              <StatusBadge status={byDay[c.day]?.status} testId={`day-${c.day}-status`} />
            </div>
            <h3 className="mt-4 font-display text-lg font-semibold text-[#0B2A4A] leading-snug">{c.title}</h3>
            <p className="mt-2 text-sm text-slate-600">{c.result}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#1A9AA8] group-hover:gap-2 transition-[gap]">Open lesson <ArrowRight className="h-4 w-4" /></span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function StudentDashboard() {
  useSeo({ title: "My course", path: "/dashboard", noindex: true });
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [app, setApp] = useState(undefined);
  const [progress, setProgress] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    api.get("/me/application").then(({ data }) => {
      setApp(data.application);
      if (data.application?.status === "approved") api.get("/course/progress").then((r) => setProgress(r.data));
    });
    api.get("/me/messages").then((r) => setMessages(r.data));
  }, []);

  if (app === undefined) return <Spinner />;
  const send = async (body) => {
    const { data } = await api.post("/me/messages", { body });
    setMessages((m) => [...m, data]);
  };
  const isHost = user.role === "host";

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-14">
      <p className="font-mono-ui text-xs tracking-[0.3em] text-[#1A9AA8]">STUDENT PROFILE · PRIVATE</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-[#0B2A4A]" data-testid="dashboard-greeting">Hi {user.name?.split(" ")[0] || "there"}</h1>
      <Tabs defaultValue={params.get("tab") || "course"} className="mt-8">
        <TabsList className="bg-[#0B2A4A]/5">
          <TabsTrigger value="course" data-testid="dashboard-tab-course">Course</TabsTrigger>
          {!isHost && <TabsTrigger value="messages" data-testid="dashboard-tab-messages">Messages</TabsTrigger>}
          <TabsTrigger value="settings" data-testid="dashboard-tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="course" className="mt-6">
          {isHost ? <Link to="/host" className="text-[#F08A3C] font-semibold">Go to the host dashboard</Link> : <>{!app ? <NoApplication /> : app.status === "approved" ? <CourseGrid progress={progress} /> : <PendingOrDeclined app={app} />}<MyOnlineCourses /></>}
        </TabsContent>
        <TabsContent value="messages" className="mt-6 max-w-2xl">
          <div className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-6">
            <h2 className="font-semibold text-[#0B2A4A] mb-4">Messages with Mr. Greg</h2>
            <MessageThread messages={messages} onSend={send} me="student" />
          </div>
        </TabsContent>
        <TabsContent value="settings" className="mt-6"><AccountSettings /></TabsContent>
      </Tabs>
    </section>
  );
}
